/**
 * PhoneInput
 *
 * Clean, country-aware phone input. Captures country code and national number,
 * formats on blur, and returns an E.164-like value via onChange.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Label } from './label';

const DEFAULT_COUNTRIES = [
  { code: 'US', dial: '+1', label: 'United States (+1)' },
  { code: 'CA', dial: '+1', label: 'Canada (+1)' },
  { code: 'GB', dial: '+44', label: 'United Kingdom (+44)' },
  { code: 'AU', dial: '+61', label: 'Australia (+61)' },
  { code: 'IN', dial: '+91', label: 'India (+91)' }
];

function normalizeDigits(value) {
  return (value || '').replace(/[^0-9]/g, '');
}

function formatNational(number) {
  // US-style formatting: (XXX) XXX-XXXX as user types
  const digits = normalizeDigits(number).slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,10)}`;
}

export default function PhoneInput({
  id = 'phone',
  name = 'phone',
  label = 'Phone number',
  countries = DEFAULT_COUNTRIES,
  defaultCountry = 'US',
  value,
  onChange,
  required = false,
  showCountrySelector = false
}) {
  const initialCountry = useMemo(() => countries.find(c => c.code === defaultCountry) || countries[0], [countries, defaultCountry]);
  const [selected, setSelected] = useState(initialCountry);
  const [national, setNational] = useState('');

  // Sync external value into local state if provided as E.164
  useEffect(() => {
    if (!value) return;
    const str = String(value);
    const match = countries.find(c => str.startsWith(c.dial));
    if (match) {
      setSelected(match);
      const raw = str.replace(match.dial, '');
      const capped = match.code === 'US' ? normalizeDigits(raw).slice(0, 10) : normalizeDigits(raw);
      setNational(capped);
    }
  }, [value, countries]);

  const handleNationalChange = (e) => {
    const raw = normalizeDigits(e.target.value);
    const digits = selected.code === 'US' ? raw.slice(0, 10) : raw;
    setNational(digits);
    if (onChange) onChange({ e164: `${selected.dial}${digits}`, country: selected.code, national: digits });
  };

  const handleCountryChange = (e) => {
    const next = countries.find(c => c.code === e.target.value) || selected;
    setSelected(next);
    if (onChange) onChange({ e164: `${next.dial}${normalizeDigits(national)}`, country: next.code, national: normalizeDigits(national) });
  };

  const displayValue = formatNational(national);

  // Ensure default to US +1 on mount if no value provided
  useEffect(() => {
    if (!value && selected && selected.code === 'US') {
      if (onChange) onChange({ e164: `${selected.dial}`, country: selected.code, national: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-2">
        {showCountrySelector ? (
          <div className="grid grid-cols-[160px_1fr] gap-3">
            <select
              aria-label="Country code"
              value={selected.code}
              onChange={handleCountryChange}
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            >
              {countries.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <input
              id={id}
              name={name}
              inputMode="tel"
              type="tel"
              placeholder="(555) 555-5555"
              value={displayValue}
              onChange={handleNationalChange}
              required={required}
              autoComplete="tel"
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            />
          </div>
        ) : (
          <input
            id={id}
            name={name}
            inputMode="tel"
            type="tel"
            placeholder="(555) 555-5555"
            value={displayValue}
            onChange={handleNationalChange}
            required={required}
            autoComplete="tel"
            className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
          />
        )}
      </div>
      {/* Hidden fields to submit clean values */}
      <input type="hidden" name="phone_country" value={selected.code} />
      <input type="hidden" name="phone_e164" value={normalizeDigits(national) ? `${selected.dial}${normalizeDigits(national)}` : ''} />
      <input type="hidden" name="phone_national" value={normalizeDigits(national)} />
      <input type="hidden" name="phone_dial" value={selected.dial} />
    </div>
  );
}


